import React, { useEffect, useLayoutEffect, useState } from "react";
import { Spin, MultiSelect } from "../../../BP2Components";
import { isFieldDisabled, isFieldHidden } from "./FormFIeldsV3Decorators";
import { getV3DataUrl } from "../../../../services/restAPI/FormsV3";

import "../formsV3.less";
import { useFieldController } from "../../../../util/useFieldController";
import { findSelectValueFromRowLabel } from "../../../../util/findSelectValueFromRowLabel";

const toSelectOption = (value) => {
   if (!value) return null;
   if (typeof value === "object" && "label" in value && "value" in value) return value;
   if (typeof value === "object" && ("decoratedValue" in value || "originalValue" in value)) {
      return {
         label: String(value.decoratedValue ?? ""),
         value: String(value.originalValue ?? ""),
      };
   }
   return { label: String(value), value: "" };
};

const toSelectValue = (value, isMulti) => {
   if (isMulti) {
      if (Array.isArray(value)) return value.map(toSelectOption).filter(Boolean);
      return value ? [toSelectOption(value)].filter(Boolean) : [];
   }
   if (Array.isArray(value)) return toSelectOption(value[0]) ?? null;
   return toSelectOption(value) ?? null;
};

const FormV3SelectField = ({
   field,
   formGUID,
   loading,
   decorators,
   isMulti = false,
   isClearable = false,
   onValueChange,
}) => {
   const {
      value: ctxValue,
      setValue: setCtxValue,
      fieldInfo,
   } = useFieldController({
      field,
      formGUID,
      onValueChange,
   });
   const [localValue, setLocalValue] = useState(() => toSelectValue(ctxValue, isMulti));

   const [optionsLoading, setOptionsLoading] = useState(false);
   const [options, setOptions] = useState();

   const normalizeValue = (val) => {
      if (isMulti) {
         if (Array.isArray(val)) return val;
         return val ? [val] : [];
      }
      if (Array.isArray(val)) return val;
      return val ?? null;
   };

   const handleValueChange = (val) => {
      setCtxValue(normalizeValue(val));
      setLocalValue(toSelectValue(normalizeValue(val), isMulti));
   };

   useEffect(() => {
      setLocalValue(toSelectValue(ctxValue, isMulti));
   }, [JSON.stringify(ctxValue)]);

   useLayoutEffect(() => {
      // Guard: skip if no dataUrl (can happen in list table edit mode)
      if (!field?.dataUrl) {
         setOptionsLoading(false);
         return;
      }

      let cancelled = false;
      setOptionsLoading(true);

      getV3DataUrl(field.dataUrl)
         .then((res) => {
            if (!cancelled) setOptions(res?.data);
         })
         .catch((err) => {
            // Silently handle errors - field may not need options in this context
            if (!cancelled) console.warn("Failed to load select options:", err);
         })
         .finally(() => {
            if (!cancelled) setOptionsLoading(false);
         });

      return () => {
         cancelled = true;
      };
   }, [field?.dataUrl]);

   useEffect(() => {
      if (!options?.length) return;
      if (typeof localValue === "string") {
         const mapped = findSelectValueFromRowLabel(localValue, options);
         setLocalValue(mapped);
         // Only propagate to context if we found a matching option
         if (mapped.value && mapped.value !== "") {
            setCtxValue(mapped);
         }
         return;
      }

      // single
      if (!isMulti) {
         if (
            localValue &&
            typeof localValue === "object" &&
            "label" in localValue &&
            "value" in localValue
         ) {
            // Only remap if value is empty AND we haven't tried mapping this label yet
            // Check if the option actually exists to prevent infinite loop when option not found
            if ((!localValue.value || localValue.value === "") && localValue.label) {
               const mapped = findSelectValueFromRowLabel(localValue.label, options);
               // Only update if we found a matching option (value is not empty)
               // This prevents infinite loop when option doesn't exist in the list
               if (mapped.value && mapped.value !== "") {
                  setLocalValue(mapped);
                  setCtxValue(mapped);
               }
            }
         }
         return;
      }

      // multiselect
      if (Array.isArray(localValue)) {
         const next = localValue.map((item) => {
            if (typeof item === "string") return findSelectValueFromRowLabel(item, options);

            if (item && item.label && (!item.value || item.value === "")) {
               const mapped = findSelectValueFromRowLabel(item.label, options);
               // Only use mapped value if option was found
               return mapped.value ? mapped : item;
            }
            return item;
         });

         if (JSON.stringify(next) !== JSON.stringify(localValue)) {
            setLocalValue(next);
            setCtxValue(next);
         }
      }
   }, [JSON.stringify(options), localValue, isMulti]);

   return (
      <>
         <span
            className={`v3-field-element formV3-element-full ${
               isFieldDisabled(field) || optionsLoading ? "hide-arrow" : ""
            }`}
         >
            <MultiSelect
               // todo: need to handle "isCreatable" when backend will be created
               isCreatable={false}
               classNamePrefix="bp2-select"
               isDisabled={isFieldDisabled(field)}
               className="bp2-select__formV3"
               customClassName={`v3-field id-${field.id} bp2-select__formV3`}
               value={localValue}
               options={options}
               isAsync={options?.length > 3000}
               isMulti={isMulti}
               isClearable={isClearable}
               closeMenuOnSelect={!isMulti}
               onChange={(e) => handleValueChange(e)}
               style={
                  fieldInfo
                     ? { borderColor: `var(--form-warnings)`, background: "#f1f5f9" }
                     : isFieldDisabled(field)
                       ? { background: "#f1f5f9", borderColor: "#cbd5e1" }
                       : {}
               }
               field={field}
            ></MultiSelect>
            {!isFieldHidden(field) && optionsLoading ? (
               <span className="spin-container">
                  <Spin className={`${field.id}`} />
               </span>
            ) : null}
            <span className="p2 dev-info">{`${field.id}`}</span>
            {fieldInfo ? <div className="validation-error">{`${fieldInfo}`}</div> : null}
         </span>
      </>
   );
};

export default FormV3SelectField;
